import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { Button, Checkbox, Form, Message, Modal } from 'semantic-ui-react';

import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import { useSelector } from 'react-redux';

import CloseableModal from 'components/common/CloseableModal';
import ConfirmModal from 'components/common/ConfirmModal';
import HelpTooltip from 'components/common/HelpTooltip';
import { ProviderEntityAttributesPropType } from 'components/modules/business-profile/types/place-action.types';
import URISchemeField from 'components/modules/fields/components/URISchemeField';
import useNavigation from 'components/modules/navigation/hooks/use-navigation';
import BehaviorSelector from 'components/modules/website-component-interaction/components/BehaviorSelector';
import { getPageForUrl } from 'components/modules/website-component-interaction/components/WebsiteComponentInteractionWidget/WebsiteComponentInteractionWidget.utils';
import {
  BEHAVIOR_EXTERNAL_LINK,
  BEHAVIOR_INTERNAL_LINK,
} from 'components/modules/website-component-interaction/constants';
import { getErrorMessage } from 'libs/errors';
import { multiCaseGet } from 'libs/format';
import Validate from 'libs/validate';
import { selectWebsitePages } from 'selectors/website';

import {
  URI_HELP_TEXT,
  IS_PREFERRED_HELP_TEXT,
  URI_PLACEHOLDER,
  CLEAR_MODAL_HEADER,
  CLEAR_MODAL_CONTENT,
} from './PlaceActionEditModal.constants';
import { getDropdownText, getInitialFormData } from './PlaceActionEditModal.utils';

import './PlaceActionEditModal.scss';

const propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  onClear: PropTypes.func,
  placeAction: ProviderEntityAttributesPropType.isRequired,
};

const defaultProps = {
  onSave: null,
  onClear: null,
};

export default function PlaceActionEditModal({
  open,
  onClose: onCloseProp,
  onSave: onSaveProp,
  onClear: onClearProp,
  placeAction,
}) {
  const pages = useSelector(selectWebsitePages);

  const { getPageUrl: getCompletePageUrl } = useNavigation({ initialFetch: false });

  const initialFormData = getInitialFormData(placeAction, pages);
  const { id: placeActionId } = placeAction;
  const { uri: initialUri } = initialFormData;
  const isAssigned = !_isEmpty(initialUri);
  const [formData, setFormData] = useState(initialFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showConfirmClear, setShowConfirmClear] = useState(false);

  const { uri, isPreferred } = formData;
  const page = getPageForUrl(uri, pages);
  const pageId = multiCaseGet(page, 'id');
  const behavior = _isNil(pageId) ? BEHAVIOR_EXTERNAL_LINK : BEHAVIOR_INTERNAL_LINK;
  const isExternal = behavior === BEHAVIOR_EXTERNAL_LINK;
  const isInternal = behavior === BEHAVIOR_INTERNAL_LINK;

  function onChange(e, { name, value, checked }) {
    const fieldValue = name === 'isPreferred' ? checked : value;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: fieldValue,
    }));
  }

  function onSelectBehavior({ behavior: newBehavior, url: newUrl }) {
    if (newBehavior === BEHAVIOR_EXTERNAL_LINK) {
      onChange({}, { name: 'uri', value: newUrl || '' });
    } else if (!_isEmpty(newUrl)) {
      onChange({}, { name: 'uri', value: newUrl });
    }
  }

  function getValidationError() {
    if (_isEmpty(uri)) {
      return 'Please enter a URL or select a page';
    }
    if (isExternal && !Validate.validate(Validate.TYPES.URI, uri)) {
      return 'Please enter a valid URL (e.g. https://example.com)';
    }
    if (isInternal && _isNil(pageId)) {
      return 'Please select a page from your website';
    }
    return null;
  }

  useEffect(() => {
    setErrorMessage(getValidationError());
  }, [formData]);

  function clearModal() {
    setFormData(getInitialFormData(placeAction, pages));
    setErrorMessage(null);
    setTouched(false);
  }

  function onClose() {
    clearModal();
    if (onCloseProp) {
      onCloseProp();
    }
  }

  function onClearClick() {
    setShowConfirmClear(true);
  }

  function onCancelClear() {
    setShowConfirmClear(false);
  }

  async function onConfirmClear() {
    try {
      setIsSaving(true);

      if (onClearProp) {
        await onClearProp(placeActionId);
      }

      onClose();
    } catch (e) {
      setErrorMessage(getErrorMessage(e));
    } finally {
      onCancelClear();
      setIsSaving(false);
    }
  }

  async function onSave() {
    setTouched(true);
    setErrorMessage(null);

    const validationErrors = getValidationError();
    if (!_isEmpty(validationErrors)) {
      setErrorMessage(validationErrors);
      return;
    }

    try {
      setIsSaving(true);

      const payload = {
        id: placeActionId,
        uri,
        pageId,
        isPreferred,
      };

      if (onSaveProp) {
        await onSaveProp(payload);
      }

      onClose();
    } catch (e) {
      setErrorMessage(getErrorMessage(e));
    } finally {
      setIsSaving(false);
    }
  }

  const { href: completePageUrl } = getCompletePageUrl(page);

  const loading = isSaving;
  const disabled = isSaving || (touched && !_isEmpty(getValidationError()));

  return (
    <>
      <CloseableModal
        open={open}
        onClose={onClose}
        className="place-action-edit-modal"
        header="Edit Place Action"
        size="small"
      >
        <Modal.Content scrolling>
          <Form>
            <Form.Field>
              <label className="label" htmlFor="uri">
                URI <HelpTooltip title="URI" content={URI_HELP_TEXT} />
              </label>
              <div className="selectors-container">
                <BehaviorSelector
                  text={getDropdownText(page)}
                  value={behavior}
                  url={uri}
                  onSelect={onSelectBehavior}
                />

                <URISchemeField
                  name="uri"
                  onChange={onChange}
                  value={isInternal ? completePageUrl : uri}
                  placeholder={URI_PLACEHOLDER}
                  required
                  className="uri"
                />
              </div>
            </Form.Field>

            <Form.Input
              label={
                <label className="label">
                  Is Primary Link{' '}
                  <HelpTooltip title="Is Primary Link" content={IS_PREFERRED_HELP_TEXT} />
                </label>
              }
              name="isPreferred"
              onChange={onChange}
              checked={isPreferred}
              className="is-preferred"
              control={Checkbox}
              toggle
            />

            {!_isEmpty(errorMessage) && touched && (
              <Message
                negative
                onDismiss={() => {
                  setErrorMessage(null);
                }}
                content={errorMessage}
              />
            )}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          {isAssigned && (
            <Button
              className="delete-red"
              content="Clear"
              onClick={onClearClick}
              disabled={loading}
            />
          )}
          <Button
            content="Save"
            className="action"
            onClick={onSave}
            loading={loading}
            disabled={disabled}
            primary
          />
        </Modal.Actions>
      </CloseableModal>

      <ConfirmModal
        open={showConfirmClear}
        onCancel={onCancelClear}
        onConfirm={onConfirmClear}
        header={CLEAR_MODAL_HEADER}
        content={CLEAR_MODAL_CONTENT}
        confirmButton={
          <Button content="Yes" loading={loading} disabled={loading} className="delete-red" />
        }
        cancelButton={<Button content="Cancel" disabled={loading} className="action-button-gray" />}
      />
    </>
  );
}

PlaceActionEditModal.propTypes = propTypes;
PlaceActionEditModal.defaultProps = defaultProps;
